import React, { FunctionComponent, MouseEventHandler, useCallback, useMemo, useState } from "react";
import "./Button.css";
import { CaretDownIcon, LargeSpinnerIcon } from "./Icon";

interface IProps {
	children?: React.ReactNode;
	color: "Red" | "Green" | "Blue";
	level: "Primary" | "Secondary" | "Tertiary";
	size: "Medium" | "Large";
	disabled?: boolean;
	leftIcon?: JSX.Element;
	rightIcon?: JSX.Element;
	inProgress?: boolean
	width?: number;
	minWidth?: number;
	title?: string;
	dropDownOptions?: string[];
	className?: string;
	onClick(option?: string): void;
}

const Button: FunctionComponent<IProps> = (props) => {
	const { children, color, level, size, disabled, leftIcon, rightIcon, inProgress, width, minWidth, title, dropDownOptions, className, onClick } = props;
	const [showDropDown, setShowDropDown] = useState(false);

	const leftSide = useMemo(() => {
		return leftIcon ? <span style={{ marginRight: "5px" }}>{leftIcon}</span> : <></>;
	}, [leftIcon]);
	const rightSide = useMemo(() => {
		if (dropDownOptions) {
			return <span style={{ marginLeft: "5px" }}><CaretDownIcon /></span>;
		}
		return rightIcon ? <span style={{ marginLeft: "5px" }}>{rightIcon}</span> : <></>;
	}, [dropDownOptions, rightIcon]);
	const spinner = useMemo(() => {
		const className = children ? "spinner" : "centeredSpinner";
		return inProgress && <span className={className}><LargeSpinnerIcon /></span>;
	}, [children, inProgress]);
	const style = useMemo(() => {
		const style: React.CSSProperties = {};
		if (width !== undefined) {
			style.width = `${width}px`;
		}
		if (minWidth !== undefined) {
			style.minWidth = `${minWidth}px`;
		}
		return style;
	}, [minWidth, width]);

	const dropDown = useMemo(() => {
		if (!dropDownOptions || !showDropDown) { return null; }

		const dropDownOptionButtons = dropDownOptions.map((value) => {
			const onSecondClick = () => onClick(value);
			return <button key={value} onClick={onSecondClick}>{value}</button>;
		});

		return <div className="dropDownDiv">
			{dropDownOptionButtons}
		</div>;
	}, [dropDownOptions, onClick, showDropDown]);
	const onFirstClick = useCallback((event: any) => {
		event.target.blur();
		if (!dropDownOptions) { onClick(); }
		setShowDropDown(oldValue => !oldValue);
	}, [dropDownOptions, onClick]);

	return <button
		type="button"
		className={`button ${color.toLowerCase()} ${level.toLowerCase()} ${size.toLowerCase()} ${className}`}
		onClick={onFirstClick}
		style={style}
		disabled={disabled}
		title={title}
	>{leftSide}<span>{children}</span>{rightSide}{spinner}{dropDown}</button>;
}

export default Button;

interface IButtonSizeProps {
	children?: React.ReactNode;
	size: "Medium" | "Large";
	width?: number;
	className?: string;
}

export const ButtonSize: FunctionComponent<IButtonSizeProps> = (props) => {
	const { children, size, width, className } = props;

	const style = useMemo(() => {
		if (width !== undefined) {
			return { width: `${width}px` }
		}
		return {};
	}, [width]);

	return <div style={style} className={`buttonSize ${size.toLowerCase()} ${className}`}>
		{children}
	</div>;
};